
import { useEffect, useState } from "react";
import Header from "@/components/Header";
import PricingTable from "@/components/PricingTable";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useSubscription } from "@/hooks/useSubscription";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, AlertTriangle, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

export default function Pricing() {
  const [searchParams] = useSearchParams();
  const success = searchParams.get("success");
  const canceled = searchParams.get("canceled");
  const sessionId = searchParams.get("session_id");
  const subscribeParam = searchParams.get("subscribe");
  
  const { refreshData, loading, error, isTestMode, currentSubscription, handleSubscribe } = useSubscription();
  const [refreshAttempted, setRefreshAttempted] = useState(false);
  const [processingWebhook, setProcessingWebhook] = useState(false);
  const [manualRefresh, setManualRefresh] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const [checkoutComplete, setCheckoutComplete] = useState(false);
  const [autoSubscribeProcessed, setAutoSubscribeProcessed] = useState(false);

  // Handle automatic subscription when arriving from Auth page
  useEffect(() => {
    if (subscribeParam && !loading && !autoSubscribeProcessed) {
      // Set flag to prevent repeated subscription attempts
      setAutoSubscribeProcessed(true);
      
      // Remove the subscribe parameter from URL to prevent subscription on refresh
      window.history.replaceState({}, document.title, '/pricing');
      
      // Trigger subscription for the selected plan
      toast.info("Preparing checkout for your selected plan...");
      handleSubscribe(subscribeParam);
    }
  }, [subscribeParam, loading, handleSubscribe, autoSubscribeProcessed]);

  // Handle subscription status messages
  useEffect(() => {
    if (success === "true" && sessionId && !refreshAttempted) {
      setProcessingWebhook(true);
      setCheckoutComplete(true);
      
      // Display info toast
      toast.info("Processing your subscription...", {
        duration: 6000
      });
      
      // Set a timeout to check for credits after allowing webhook time to process
      const checkTimer = setTimeout(() => {
        console.log("Forcing data refresh after checkout");
        refreshData();
        setRefreshAttempted(true);
        
        // Schedule multiple refreshes to ensure credits are updated
        // This accounts for potential webhook processing delays
        for (let i = 1; i <= 5; i++) {
          setTimeout(() => {
            console.log(`Scheduled refresh attempt ${i}`);
            refreshData();
            setRefreshCount(prev => prev + 1);
            
            // Only show success after final refresh attempt
            if (i === 5) {
              setProcessingWebhook(false);
              toast.success("Your payment has been processed. If subscription isn't updated, try the refresh button.", {
                duration: 10000
              });
            }
          }, i * 4000); // 4s, 8s, 12s, 16s, 20s
        }
      }, 2000);
      
      return () => clearTimeout(checkTimer);
    } else if (canceled === "true") {
      toast.info("Subscription canceled. You can try again anytime.");
    }
  }, [success, canceled, sessionId, refreshData, refreshAttempted]);

  const handleManualRefresh = async () => {
    setManualRefresh(true);
    refreshData();
    toast.info("Refreshing subscription data...");
    
    // If we have a session ID and no subscription update yet, try manual activation
    if (sessionId && !currentSubscription?.plan_id?.includes("yearly") && !currentSubscription?.plan_id?.includes("monthly")) {
      try {
        toast.info("Attempting to manually activate your subscription...");
        
        // First try to get the checkout session to verify details
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          // Request manual activation via edge function
          const result = await fetch('https://ksibxkytufszpgsrapjz.supabase.co/functions/v1/stripe-webhook', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              type: 'debug.force_update',
              data: {
                object: {
                  userId: user.id,
                  // If yearly subscription success, use yearly plan ID
                  planId: "41a539b7-7303-4a98-ade9-2ccbe8f793cb" // yearly plan ID
                }
              }
            })
          });
          
          if (result.ok) {
            toast.success("Manual activation initiated. Your subscription should be active shortly.");
          } else {
            console.error("Manual activation failed:", await result.text());
            toast.error("Manual activation failed. Please contact support.");
          }
        }
      } catch (error) {
        console.error("Error during manual activation:", error);
      }
    }
    
    setTimeout(() => {
      setManualRefresh(false);
      refreshData(); // Double refresh to ensure latest data
      toast.success("Subscription data refreshed");
    }, 2000);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold tracking-tight text-primary sm:text-4xl mb-2">
            Choose Your CraftyJoy Plan
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Transform everyday objects into creative projects with your children
          </p>
        </div>

        {isTestMode && (
          <Alert className="mb-6 bg-yellow-50 border-yellow-200">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <AlertTitle className="text-yellow-700">Stripe Test Mode Active</AlertTitle>
            <AlertDescription className="text-yellow-600">
              Use test card: 4242 4242 4242 4242 with any future expiry date, CVC and ZIP code.
              Transactions won't be charged in test mode.
            </AlertDescription>
          </Alert>
        )}

        {(checkoutComplete || refreshCount > 0) && (
          <div className="mb-6 flex justify-end">
            <Button 
              onClick={handleManualRefresh} 
              variant="outline" 
              size="sm"
              className="flex items-center gap-2"
              disabled={manualRefresh || loading}
            >
              {manualRefresh ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Refresh Subscription
            </Button>
          </div>
        )}

        {processingWebhook && (
          <Alert className="mb-6 bg-blue-50 border-blue-200">
            <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
            <AlertTitle>Processing Subscription</AlertTitle>
            <AlertDescription>
              We're processing your subscription. This may take up to 30 seconds. Your subscription will be updated shortly.
              If it doesn't appear after waiting, please try the refresh button.
            </AlertDescription>
          </Alert>
        )}

        {success === "true" && !processingWebhook && (
          <Alert className="mb-6 bg-green-50 border-green-200">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <AlertTitle>Transaction Completed</AlertTitle>
            <AlertDescription>
              Your transaction has been processed. If you don't see your subscription updated, please use the refresh button 
              above or reload the page. Your account should be updated within 30 seconds.
            </AlertDescription>
          </Alert>
        )}

        {error && !processingWebhook && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <PricingTable />
      </main>
    </div>
  );
}
